import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatService } from '../services/chatService';
import RfiContainer from '../components/rfi/RfiContainer';
import KnowledgeTextInput from '../components/knowledge/KnowledgeTextInput';
import KnowledgeFileUpload from '../components/knowledge/KnowledgeFileUpload';
import KnowledgeDocuments from '../components/knowledge/KnowledgeDocuments';
import Chat, { ChatRef } from '../components/chat-working/Chat';
import ChatLayout from '../components/chat-working/ChatLayout';
import ChatSidebar, { Conversation } from '../components/chat-working/ChatSidebar';
import { useSidebarState } from '../hooks/useSidebarState';

interface HomeProps {
  selectedPage: string;
  onPageSelect: (page: string) => void;
}

const Home: React.FC<HomeProps> = ({ selectedPage, onPageSelect }) => {
  const navigate = useNavigate();
  const chatRef = useRef<ChatRef>(null);
  const { isCollapsed, toggleSidebar } = useSidebarState();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [showTextModal, setShowTextModal] = useState(false);
  const [citationTextContent, setCitationTextContent] = useState<string>('');
  const [citationTitle, setCitationTitle] = useState<string>('');
  const [citationAsOfDate, setCitationAsOfDate] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(undefined);

  // Set default page to Query engine on mount
  useEffect(() => {
    if (!selectedPage) {
      onPageSelect('Query engine');
    }
    console.warn('[Home] Mounted - citation handler ready. Build includes citation title/date fix.');
  }, []);

  // Load conversations for Query engine
  useEffect(() => {
    if (selectedPage === 'Query engine') {
      loadConversations();
    }
  }, [selectedPage]);

  const loadConversations = async () => {
    try {
      const response = await chatService.getChatHistory(1, 100);
      const data = response.data;
      const threads = data.items || [];

      const convs: Conversation[] = threads.map((thread: any) => ({
        id: thread.thread_id,
        title: thread.title || 'Untitled Chat',
        lastMessage: thread.title,
        timestamp: thread.created_at ? new Date(thread.created_at) : undefined
      }));

      setConversations(convs);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConversations([]);
    }
  };

  const handleSelectConversation = async (convId: string) => {
    try {
      const response = await chatService.getThreadMessages(convId);
      const data = response.data;
      if (data.conversations && chatRef.current) {
        chatRef.current.handleThreadSelect(convId, data.conversations);
        setCurrentConversationId(convId);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleNewChat = () => {
    // Clear chat but stay on Query engine page
    if (chatRef.current) {
      chatRef.current.handleThreadSelect('', []);
      setCurrentConversationId(undefined);
    }
    // Reload conversations to show the previous thread in history
    loadConversations();
  };


  // Format date to MM-DD-YYYY
  const formatAsOfDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr || dateStr === 'None') return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    } catch {
      return null;
    }
  };

  const getTitleFromCitation = (citation?: { file_name?: string; fund?: string } | null) => {
    if (!citation) return null;
    return citation.file_name || citation.fund || null;
  };

  const handleCitationClick = async (
    citationId: string,
    page: number,
    citationData?: { file_name?: string; fund?: string; as_of_date?: string }
  ) => {
    console.warn('[Home] handleCitationClick CALLED - id:', citationId);
    try {
      // Debug: trace citation flow (remove in production if desired)
      console.warn('[Citation] citationId:', citationId, 'citationData:', citationData);

      // Use passed citation data for immediate display (from conversation/thread)
      const titleFromCitation = getTitleFromCitation(citationData);
      const dateFromCitation = citationData?.as_of_date;
      console.log('[Citation] titleFromCitation:', titleFromCitation, 'dateFromCitation:', dateFromCitation);

      if (titleFromCitation || dateFromCitation) {
        setCitationTitle(titleFromCitation || '');
        setCitationAsOfDate(formatAsOfDate(dateFromCitation));
      }

      // When no citation data, fetch metadata FIRST so we have title/date before showing modal
      if (!titleFromCitation) {
        try {
          const meta = await chatService.getCitationMetadata(citationId);
          console.log('[Citation] Metadata API response:', meta);
          setCitationTitle(meta.title || 'Citation PDF');
          setCitationAsOfDate(formatAsOfDate(meta.as_of_date));
        } catch (metaErr) {
          console.error('[Citation] Metadata API failed:', metaErr);
          setCitationTitle('Citation PDF');
          setCitationAsOfDate(null);
        }
      }

      // Fetch citation as blob
      const blob = await chatService.getCitationPdf(citationId);
      console.log('Citation blob type:', blob.type, 'size:', blob.size);

      // Check if response is JSON (text citation) or PDF
      if (blob.type === 'application/json' || blob.size < 1000) {
        // Try to parse as JSON (text citation)
        try {
          const text = await blob.text();
          const data = JSON.parse(text);
          console.log('Citation Text:', data.text_content);
          setCitationTextContent(data.text_content || 'No text content available');
          const title = data.file_name || data.title || data.fund || 'Citation Text';
          setCitationTitle(title);
          setCitationAsOfDate(formatAsOfDate(data.as_of_date));
          setShowTextModal(true);
        } catch (parseError) {
          // If parse fails, treat as PDF - metadata already fetched above if needed
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setPdfPage(page);
          setShowPdfModal(true);
        }
      } else {
        // It's a PDF (large blob) - metadata already fetched above if needed
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPdfPage(page);
        setShowPdfModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch citation:', error);
    }
  };

  const truncateText = (text: string, maxLines: number = 3) => {
    const lines = text.split('\n');
    if (lines.length <= maxLines) return text;
    
    const truncated = lines.slice(0, maxLines).join('\n');
    return truncated + '...';
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      // Any cleanup needed
    };
  }, [selectedPage]);

  const renderLeftContent = () => {
    if (selectedPage === 'Query engine') {
      return null; // EmbeddedChat will handle its own layout
    }

    if (selectedPage === 'RFI File Upload') {
      return null; // RfiContainer will handle its own layout
    }

    return null;
  };

  const renderRightContent = () => {
    if (selectedPage === 'Query engine') {
      return (
        <ChatLayout
          sidebarCollapsed={isCollapsed}
          sidebar={
            <ChatSidebar
              conversations={conversations}
              currentConversationId={currentConversationId}
              collapsed={isCollapsed}
              onToggleCollapse={toggleSidebar}
              onNewChat={handleNewChat}
              onSelectConversation={handleSelectConversation}
              onRenameConversation={(id, title) => {
                setConversations(prev =>
                  prev.map(c => c.id === id ? { ...c, title } : c)
                );
              }}
              onDeleteConversation={(id) => {
                setConversations(prev => prev.filter(c => c.id !== id));
              }}
            />
          }
        >
          <Chat
            ref={chatRef}
            onCitationClick={handleCitationClick}
            onToggleSidebar={toggleSidebar}
            conversationTitle="New Chat"
          />
        </ChatLayout>
      );
    }
    if (selectedPage === 'RFI File Upload') {
      return (
        <RfiContainer onCitationClick={handleCitationClick} />
      );
    }
    if (selectedPage === 'Add knowledge') {
      return (
        <div className="card-elevated">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Add Knowledge</h1>

          <div className="space-y-6">
            <KnowledgeTextInput />

            <hr className="border-gray-200" />

            <KnowledgeFileUpload />
          </div>
        </div>
      );
    }
    if (selectedPage === 'Knowledge Library') {
      return (
        <KnowledgeDocuments />
      );
    }
    return (
      <div className="card-elevated text-center">
        <h2 className="text-4xl font-bold text-gray-800 mb-4">Evolution Credit Partners RFI Engine</h2>
        <p className="text-lg text-gray-600">Select a page to get started</p>
      </div>
    );
  };

  // Special handling for Query engine to break out of container
  if (selectedPage === 'Query engine') {
    return (
      <>
        <div className="fixed inset-0 top-[4rem] bottom-0 bg-white">
          {renderRightContent()}
        </div>

        {/* PDF Modal */}
        {showPdfModal && pdfUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {citationTitle || 'Citation PDF'}
                  {citationAsOfDate && (
                    <span className="text-gray-500 font-normal ml-2">
                      (As of {citationAsOfDate})
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => {
                    setShowPdfModal(false);
                    URL.revokeObjectURL(pdfUrl);
                    setPdfUrl(null);
                    setCitationAsOfDate(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <iframe
                src={`${pdfUrl}#page=${pdfPage}`}
                className="flex-1 w-full border rounded"
                title="Citation PDF"
              />
            </div>
          </div>
        )}

        {/* Text Content Modal */}
        {showTextModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl h-5/6 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {citationTitle}
                  {citationAsOfDate && (
                    <span className="text-gray-500 font-normal ml-2">
                      (As of {citationAsOfDate})
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => {
                    setShowTextModal(false);
                    setCitationTextContent('');
                    setCitationTitle('');
                    setCitationAsOfDate(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4 rounded border">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                  {citationTextContent}
                </pre>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen space-y-12">
      {!selectedPage && (
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">DDQ</h1>
          <p className="text-lg text-gray-600">Choose a page to get started</p>
        </div>
      )}

      {selectedPage && (
        selectedPage === 'Add knowledge' || selectedPage === 'RFI File Upload' || selectedPage === 'Knowledge Library' ? (
          <div className="w-full">
            {renderRightContent()}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              {renderLeftContent()}
            </div>
            <div className="lg:col-span-2">
              {renderRightContent()}
            </div>
          </div>
        )
      )}

      {/* PDF Modal */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {citationTitle || 'Citation PDF'}
                {citationAsOfDate && (
                  <span className="text-gray-500 font-normal ml-2">
                    (As of {citationAsOfDate})
                  </span>
                )}
              </h3>
              <button 
                onClick={() => {
                  setShowPdfModal(false);
                  URL.revokeObjectURL(pdfUrl);
                  setPdfUrl(null);
                  setCitationAsOfDate(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <iframe 
              src={`${pdfUrl}#page=${pdfPage}`}
              className="flex-1 w-full border rounded"
              title="Citation PDF"
            />
          </div>
        </div>
      )}

      {/* Text Content Modal */}
      {showTextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {citationTitle}
                {citationAsOfDate && (
                  <span className="text-gray-500 font-normal ml-2">
                    (As of {citationAsOfDate})
                  </span>
                )}
              </h3>
              <button 
                onClick={() => {
                  setShowTextModal(false);
                  setCitationTextContent('');
                  setCitationTitle('');
                  setCitationAsOfDate(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50 p-4 rounded border">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                {citationTextContent}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 